import { inject, Injectable, PLATFORM_ID, signal } from '@angular/core';
import { isPlatformBrowser } from '@angular/common';
import { PrimeNG } from 'primeng/config';
import { definePreset } from '@primeng/themes';

import Aura     from '@primeng/themes/aura';
import Lara     from '@primeng/themes/lara';
import Nora     from '@primeng/themes/nora';
import Material from '@primeng/themes/material';

export interface ColorPalette {
  key:       string;
  label:     string;
  swatch:    string;
  tokenBase: string;  // PrimeNG built-in palette name
}

export const COLOR_PALETTES: ColorPalette[] = [
  { key: 'indigo',  label: 'Indigo',   swatch: '#6366f1', tokenBase: 'indigo'   },
  { key: 'violet',  label: 'Violet',   swatch: '#8b5cf6', tokenBase: 'purple'   },
  { key: 'blue',    label: 'Blue',     swatch: '#3b82f6', tokenBase: 'blue'     },
  { key: 'teal',    label: 'Teal',     swatch: '#14b8a6', tokenBase: 'teal'     },
  { key: 'emerald', label: 'Emerald',  swatch: '#10b981', tokenBase: 'green'    },
  { key: 'rose',    label: 'Rose',     swatch: '#f43f5e', tokenBase: 'pink'     },
  { key: 'amber',   label: 'Amber',    swatch: '#f59e0b', tokenBase: 'orange'   },
  { key: 'cyan',    label: 'Cyan',     swatch: '#06b6d4', tokenBase: 'cyan'     },
];

const SHADES = [50, 100, 200, 300, 400, 500, 600, 700, 800, 900, 950];

function buildPrimary(tokenBase: string): Record<number, string> {
  return SHADES.reduce((acc, s) => { acc[s] = `{${tokenBase}.${s}}`; return acc; }, {} as Record<number, string>);
}

const BASE_PRESETS: Record<string, any> = { aura: Aura, lara: Lara, nora: Nora, material: Material };

const STORAGE_COLOR  = 'app-color-palette';
const STORAGE_PRESET = 'app-theme-preset';
const STORAGE_DARK   = 'app-dark-mode';

@Injectable({ providedIn: 'root' })
export class ThemeService {
  private platformId = inject(PLATFORM_ID);
  private primeng    = inject(PrimeNG);

  palettes        = COLOR_PALETTES;
  activePalette   = signal<string>('indigo');
  activePreset    = signal<string>('aura');

  constructor() {
    if (isPlatformBrowser(this.platformId)) {
      const color  = localStorage.getItem(STORAGE_COLOR)  ?? 'indigo';
      const preset = localStorage.getItem(STORAGE_PRESET) ?? 'aura';
      const dark   = localStorage.getItem(STORAGE_DARK)   === 'true';

      this.activePreset.set(preset);
      this.activePalette.set(color);
      this._applyPreset(preset, color);
      if (dark) document.documentElement.classList.add('dark-mode');
    }
  }

  // ── Public API ────────────────────────────────────────────────

  setColorPalette(key: string): void {
    const palette = COLOR_PALETTES.find(p => p.key === key);
    if (!palette) return;
    this.activePalette.set(key);
    if (isPlatformBrowser(this.platformId)) {
      localStorage.setItem(STORAGE_COLOR, key);
    }
    this._applyPreset(this.activePreset(), key);
  }

  setTheme(name: string): void {
    if (!BASE_PRESETS[name]) return;
    this.activePreset.set(name);
    if (isPlatformBrowser(this.platformId)) {
      localStorage.setItem(STORAGE_PRESET, name);
    }
    this._applyPreset(name, this.activePalette());
  }

  toggleDarkMode(enable: boolean): void {
    if (!isPlatformBrowser(this.platformId)) return;
    enable
      ? document.documentElement.classList.add('dark-mode')
      : document.documentElement.classList.remove('dark-mode');
    localStorage.setItem(STORAGE_DARK, String(enable));
  }

  isDarkMode(): boolean {
    if (!isPlatformBrowser(this.platformId)) return false;
    return document.documentElement.classList.contains('dark-mode');
  }

  // ── Private ───────────────────────────────────────────────────

  private _applyPreset(presetName: string, colorKey: string): void {
    const base    = BASE_PRESETS[presetName] ?? Aura;
    const palette = COLOR_PALETTES.find(p => p.key === colorKey) ?? COLOR_PALETTES[0];

    const themed = definePreset(base, {
      semantic: {
        primary: buildPrimary(palette.tokenBase)
      }
    });

    this.primeng.theme.set({
      preset:  themed,
      options: { darkModeSelector: '.dark-mode' }
    });
  }
}
